import { useParams } from "react-router-dom";
import { useUser } from "../../../../contexts/userContext/userContext";
import {
  BillingPaymentGatewayE,
  PaymentTypeE,
} from "../models/enums";
import {
  selectCreditCards,
  selectSelectedCreditCardId,
  selectPaymentType,
  selectIsCreditCardsLoading,
  selectIsPurchaseFailed,
} from "../store/purchasesSelectors";
import {
  setSelectedCreditCardId,
  setPaymentType,
  setCreditCards,
  setIsCreditCardsLoading,
  setIsPurchaseFailed,
} from "../store/purchasesSlice";
import { getAllCreditCardsByUserId } from "../store/purchasesThunks";
import { useAppDispatch, useAppSelector } from "./../../../../store/hooks";
import { useCallback } from "react";
import type { PackageTypeRouteT } from "../models/models";
import type { BillingDetailsT } from "../../../../models/models";

/**
 * Custom hook for managing credit card related functionality.
 *
 * @returns An object containing various functions and state values related to credit cards.
 */
const useCreditCard = () => {
  const { packageType } = useParams<{
    packageType: PackageTypeRouteT;
  }>();
  const dispatch = useAppDispatch();
  const { getUserData, getUserBillingDetails, isSubscribed } = useUser();
  const billingDetails = getUserBillingDetails() as BillingDetailsT;
  const isPurchaseFailed = useAppSelector(selectIsPurchaseFailed);

  const creditCards = useAppSelector(selectCreditCards);
  const selectedCreditCardId = useAppSelector(selectSelectedCreditCardId);
  const isCreditCardsLoading = useAppSelector(selectIsCreditCardsLoading);
  const paymentType = useAppSelector(selectPaymentType);

  const updatePaymentType = useCallback(
    (paymentType: PaymentTypeE) => {
      dispatch(setPaymentType(paymentType));
    },
    [dispatch]
  );

  const changeSelectedCreditCardId = useCallback(
    (id: number) => {
      dispatch(setSelectedCreditCardId(id));
    },
    [dispatch]
  );

  const fetchCreditCards = useCallback((isAddon?: boolean) => {
    if (getUserData()?.UserId) {
      const payload = {
        userId: getUserData()?.UserId,
        lastPaymentInPayPal: billingDetails?.payment_gateway === "PayPal",
        isAddon: !!isAddon,
      };
      dispatch(getAllCreditCardsByUserId(payload));
    } else {
      dispatch(setIsCreditCardsLoading(false));
    }
  }, [billingDetails?.payment_gateway, dispatch, getUserData()?.UserId]);

  const getCreditCardById = useCallback(
    (providedId: number) => {
      return creditCards?.find(({ id }) => providedId === id);
    },
    [creditCards]
  );

  const clearCreditCardList = useCallback(() => {
    dispatch(setCreditCards([]));
  }, []);

  const updateIsPurchaseFailed = (value: boolean) => {
    dispatch(setIsPurchaseFailed(value));
  };

  /**
   * Since we can have two separated billing cycles for subscription and addons,
   * it is possible to use different payment methods for each cycle.
   *
   * Here we set the payment method and credit card according to the billing cycle.
   *
   * WHAT IT DOES:
   * If the billing cycle is related to addons, it checks if the user has already purchased addons.
   * If addons are purchased, it sets the payment method based on the addons' payment method.
   * If the payment method is PayPal, it removes the selected credit card and sets the payment type as PayPal.
   * If the payment method is not PayPal, it sets the selected credit card
   * based on the last payment token ID and sets the payment type as credit card.
   * If no addons are purchased, it checks if a credit card exists. If it does,
   * it sets the selected credit card based on the token ID. If not, it sets the payment type as PayPal.
   *
   * If the billing cycle is not related to addons, it checks if the user is subscribed.
   * If subscribed, it sets the payment method based on the subscription's payment method.
   * If the payment method is PayPal, it removes the selected credit card and sets the payment type as PayPal.
   * If the payment method is not PayPal, it sets the selected credit card based
   * on the last payment token ID and sets the payment type as credit card.
   * If not subscribed, it does nothing.
   */
  const setPaymentMethodAndCreditCardAccordingToBillingCycle = () => {
    if (!billingDetails) return;

    // Is current billing cycle related to the addons
    const isAddons =
      packageType === "sub-users" || packageType === "extra-storage";

    // Check if user already purchased addons
    const areAddonsExist = !!billingDetails.addons?.length;

    if (isAddons) {
      if (areAddonsExist) {
        const addon = billingDetails.addons!?.[0];
        const addonsBillingCyclePaymentMethod = addon.payment_method;

        // If payment_gateway equal to PayPal, it means the payment method,
        // using for addons used last time was PayPal.
        //
        // In this case, remove selected credit card and set current payment method as PayPal.
        if (addonsBillingCyclePaymentMethod === BillingPaymentGatewayE.PayPal) {
          changeSelectedCreditCardId(0);
          updatePaymentType(PaymentTypeE.PAYPAL);
        } else {
          const lastPaymentTokenId = addon.token_id as number;

          changeSelectedCreditCardId(lastPaymentTokenId || 0);
          updatePaymentType(PaymentTypeE.CREDIT_CARD);
        }
      } else {
        // If no addons purchased yet

        // If credit card exist
        if (billingDetails.token_id) {
          changeSelectedCreditCardId(billingDetails.token_id);
        } else {
          updatePaymentType(PaymentTypeE.PAYPAL);
        }
      }
    } else {
      if (isSubscribed()) {
        const subscriptionPaymentMethod = billingDetails.payment_gateway;

        if (subscriptionPaymentMethod === BillingPaymentGatewayE.PayPal) {
          changeSelectedCreditCardId(0);
          updatePaymentType(PaymentTypeE.PAYPAL);
        } else {
          const lastPaymentTokenId = billingDetails.token_id as number;

          changeSelectedCreditCardId(lastPaymentTokenId || 0);
          updatePaymentType(PaymentTypeE.CREDIT_CARD);
        }
      }

      // If not subscribed, do nothing
    }
  };

  return {
    fetchCreditCards,
    creditCards,
    selectedCreditCardId,
    setSelectedCreditCardId: changeSelectedCreditCardId,
    isCreditCardsLoading,
    paymentType,
    setPaymentType: updatePaymentType,
    getCreditCardById,
    clearCreditCardList,
    isPurchaseFailed,
    setIsPurchaseFailed: updateIsPurchaseFailed,
    setPaymentMethodAndCreditCardAccordingToBillingCycle,
  };
};

export default useCreditCard;
